/**
 * Pinch-to-zoom for the studio's check canvases.
 *
 * Why this exists: on a phone in landscape, Princewill pinch-zooms
 * the page to see the check clearly - but page zoom scales the
 * studio's fixed chrome (selection pill, bottom dock) with it, and
 * the buttons grow until they cover the check. The fix is to zoom
 * the CANVAS, not the page: two fingers on a check canvas drive the
 * studio's own view transform (the `.oc-canvas-scale` wrapper gains
 * a pinch multiplier + pan on top of the fit zoom), and page-level
 * pinch zoom is locked out while the studio is open.
 *
 * Coordinate model (all CSS px, client frame):
 *   screen(p) = origin + t + base * k * p
 * where `origin` is the client position of the scale wrapper's
 * untransformed top-left corner, `base` is the studio's fit zoom
 * (effectiveZoom, owned by CheckBuilder), and { k, tx, ty } is the
 * pinch view this module manages: k is a multiplier on top of base
 * (1 = fit, exactly the pre-pinch layout) and (tx, ty) the pan.
 * Steps are computed incrementally from the two touch points, so no
 * absolute frame is needed beyond the origin at gesture time.
 *
 * Fabric coexistence: fabric's build here has the gestures module
 * excluded and drives objects from a single "main" touch. The DOM
 * wiring below therefore intercepts the gesture in the CAPTURE
 * phase on the canvas element: the second finger's touchstart is
 * swallowed before fabric sees it, every touchmove is swallowed
 * while the pinch is live (fabric's in-flight drag simply freezes),
 * and the final touchend (last finger up) is let through so fabric
 * finalizes its drag with zero net movement. Selection, drags,
 * long-press and multi-select taps are single-finger flows and are
 * untouched; a `oc:pinchstart` CustomEvent on the element lets the
 * selection wiring cancel any press it was tracking for finger two.
 */

export interface PinchPoint { x: number; y: number }

export interface PinchView { k: number; tx: number; ty: number }

export const PINCH_MIN_SCALE = 1;
export const PINCH_MAX_SCALE = 4;

export const IDENTITY_PINCH_VIEW: PinchView = { k: 1, tx: 0, ty: 0 };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function pinchMidpoint(a: PinchPoint, b: PinchPoint): PinchPoint {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function pinchDistance(a: PinchPoint, b: PinchPoint): number {
    return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * One incremental pinch step: the two touch points moved from
 * (prevA, prevB) to (nextA, nextB) (same fingers, same order).
 * The content point under the previous midpoint lands exactly
 * under the new midpoint, and the scale multiplies by the distance
 * ratio - clamped, with the ratio recomputed from the CLAMPED
 * scale so the midpoint invariance survives clamping. Pinching
 * back to (or past) fit scale snaps home to the identity view:
 * an un-zoom always returns the check exactly to its fitted place.
 */
export function pinchStep(
    view: PinchView,
    origin: PinchPoint,
    prevA: PinchPoint, prevB: PinchPoint,
    nextA: PinchPoint, nextB: PinchPoint
): PinchView {
    const dPrev = pinchDistance(prevA, prevB);
    const dNext = pinchDistance(nextA, nextB);
    if (dPrev <= 0 || dNext <= 0) return view;
    const k = clamp(view.k * (dNext / dPrev), PINCH_MIN_SCALE, PINCH_MAX_SCALE);
    if (k <= PINCH_MIN_SCALE) return { ...IDENTITY_PINCH_VIEW };
    const r = k / view.k;
    const midPrev = pinchMidpoint(prevA, prevB);
    const midNext = pinchMidpoint(nextA, nextB);
    // u = screen offset of the anchored content point from the
    // translated origin before the step; after the step the same
    // point sits at midNext: t' = (midNext - origin) - u * r.
    const ux = midPrev.x - origin.x - view.tx;
    const uy = midPrev.y - origin.y - view.ty;
    return { k, tx: midNext.x - origin.x - ux * r, ty: midNext.y - origin.y - uy * r };
}

/**
 * Keep the pan from losing the check, clamped in the VIEWPORT
 * frame: the frame the user actually sees through.
 *
 * Why the frame matters (the 2026-11-04 "pinch pan barely travels"
 * defect): the pan (tx, ty) lives in the scale wrapper's layout
 * frame, whose origin sits at (offsetX, offsetY) inside the
 * visible viewport - and that offset is NOT zero in real use. The
 * two-page column is taller than a landscape phone's visible area
 * and the canvas scroller / page scrolls, so the wrapper's top can
 * sit hundreds of px above the visible window. Clamping in the
 * wrapper frame with only a fixed ±48px of overshoot past the
 * FITTED edges then strands whole regions of the zoomed check:
 * bringing them into the window needs pan values the wrapper-frame
 * clamp forbids (e.g. returning the front check's top into a short
 * landscape window needs ty of several hundred; the old clamp
 * capped ty at +48 outright).
 *
 * The rule, per axis, applied to s = offset + t (the content's
 * top-left in viewport coords) against the visible box [0, box]
 * and the scaled content size `visual`:
 *   centred home  sHome = (box - visual) / 2
 *   slack         S = max(0, (visual - box) / 2) + margin
 *   s is clamped to [sHome - S, sHome + S]
 * so the pan range GROWS with the zoom (at 2x/4x every point of
 * the check can be brought into the window, each content edge can
 * be pulled to within `margin` of the viewport's near edge), and
 * the check can never be lost (no content edge can be pushed past
 * the viewport centre by more than the geometry above allows -
 * at the extremes the opposite edge is still inside the window).
 * With offset 0 and content larger than the box (the historical
 * caller's case) this reduces exactly to the previous wrapper-
 * frame clamp, [box - visual - margin, +margin]; content smaller
 * than the box now pans around its centred home instead.
 */
export function clampPinchPan(
    view: PinchView,
    boxW: number, boxH: number,
    visualW: number, visualH: number,
    margin = 48,
    offsetX = 0,
    offsetY = 0
): PinchView {
    if (view.k <= PINCH_MIN_SCALE) return { ...IDENTITY_PINCH_VIEW };
    const clampAxis = (t: number, box: number, visual: number, offset: number) => {
        const sHome = (box - visual) / 2;
        const slack = Math.max(0, (visual - box) / 2) + margin;
        return clamp(offset + t, sHome - slack, sHome + slack) - offset;
    };
    return {
        k: view.k,
        tx: clampAxis(view.tx, boxW, visualW, offsetX),
        ty: clampAxis(view.ty, boxH, visualH, offsetY)
    };
}

/**
 * The visible viewport frame for a canvas element: the canvas
 * scroller's client rect intersected with the window, plus the
 * pinch origin's offset inside that frame. Returns null when the
 * frame cannot be measured (no scroller ancestor, or a degenerate
 * rect - jsdom, hidden tab), in which case the caller falls back
 * to the historical wrapper-frame clamp.
 */
export function pinchViewportFrame(
    el: HTMLElement,
    origin: PinchPoint
): { w: number; h: number; offsetX: number; offsetY: number } | null {
    if (typeof window === 'undefined' || !el || typeof el.closest !== 'function') return null;
    const scroller = el.closest('.canvas-wrapper-scroll');
    if (!scroller) return null;
    const r = scroller.getBoundingClientRect();
    const left = Math.max(0, r.left);
    const top = Math.max(0, r.top);
    const right = Math.min(window.innerWidth || 0, r.right);
    const bottom = Math.min(window.innerHeight || 0, r.bottom);
    const w = right - left;
    const h = bottom - top;
    if (!(w > 0) || !(h > 0)) return null;
    return { w, h, offsetX: origin.x - left, offsetY: origin.y - top };
}

/** The wrapper transform for a pinch view on top of the fit zoom. */
export function pinchCssTransform(view: PinchView, baseScale: number): string {
    return `translate(${view.tx}px, ${view.ty}px) scale(${baseScale * view.k})`;
}

/** Double-tap camera toggle target (Princewill's gesture map). */
export const DOUBLE_TAP_ZOOM = 2.5;

/**
 * Zoom the camera to `targetK` anchored at a client point: the
 * content point under the point stays under the point. Same
 * anchor algebra as pinchStep (t' = point - origin - u * r with
 * r = k'/k and u the anchored point's offset), with the scale
 * clamped to the pinch range; a target at (or below) fit scale
 * returns the identity view, exactly like pinching home.
 */
export function anchorZoom(
    view: PinchView,
    origin: PinchPoint,
    point: PinchPoint,
    targetK: number
): PinchView {
    const k = clamp(targetK, PINCH_MIN_SCALE, PINCH_MAX_SCALE);
    if (k <= PINCH_MIN_SCALE) return { ...IDENTITY_PINCH_VIEW };
    const r = k / view.k;
    const ux = point.x - origin.x - view.tx;
    const uy = point.y - origin.y - view.ty;
    return { k, tx: point.x - origin.x - ux * r, ty: point.y - origin.y - uy * r };
}

export interface PinchZoomOptions {
    /** Current pinch view (source of truth lives with the caller). */
    getView: () => PinchView;
    /** Client coords of the scale wrapper's untransformed top-left. */
    getOrigin: () => PinchPoint;
    /** Layout box + current visual size, for pan clamping (null = no clamp). */
    getClampBox?: () => { boxW: number; boxH: number; visualW: number; visualH: number } | null;
    onStart?: () => void;
    onMove?: (view: PinchView) => void;
    onEnd?: (view: PinchView) => void;
}

/**
 * Wire two-finger pinch on ONE canvas element to the shared pinch
 * view. Attach to both studio canvases (front + back) with the same
 * options so either one drives the same view. Returns a detach
 * function. Single-finger touches are never intercepted.
 */
export function attachPinchZoom(el: HTMLElement, opts: PinchZoomOptions): () => void {
    // Defensive: a missing element must NEVER throw here. This runs
    // inside canvas-init effects, where a throw unmounts the whole
    // studio (the 2026-11-04 mobile-zoom build shipped
    // `canvas.upperCanvas` — a property fabric 5.5 does not have;
    // the element is `upperCanvasEl` — and blanked the entire app
    // on first render in every real browser while jsdom tests,
    // whose fabric stub invented the wrong property, stayed green).
    // No element simply means no pinch on that canvas.
    if (!el || typeof el.addEventListener !== 'function') return () => { };
    let active = false;
    let last: [PinchPoint, PinchPoint] | null = null;
    let lastPair = '';

    const points = (e: TouchEvent): [PinchPoint, PinchPoint] => [
        { x: e.touches[0].clientX, y: e.touches[0].clientY },
        { x: e.touches[1].clientX, y: e.touches[1].clientY }
    ];
    const pairOf = (e: TouchEvent) => `${e.touches[0].identifier}:${e.touches[1].identifier}`;

    const begin = (e: TouchEvent) => {
        active = true;
        last = points(e);
        lastPair = pairOf(e);
        // Let sibling wiring on this element (selection / long-press)
        // abandon whatever the second finger looked like to it.
        el.dispatchEvent(new CustomEvent('oc:pinchstart', { bubbles: true }));
        opts.onStart?.();
    };

    const stepTo = (e: TouchEvent) => {
        if (!last || e.touches.length !== 2) return;
        // A different finger pair than the baseline (a third finger
        // came and went): recapture the baseline, do not step - a
        // step from a stale pair would jump the view.
        if (pairOf(e) !== lastPair) { last = points(e); lastPair = pairOf(e); return; }
        const pts = points(e);
        const origin = opts.getOrigin();
        let next = pinchStep(opts.getView(), origin, last[0], last[1], pts[0], pts[1]);
        const box = opts.getClampBox?.();
        if (box) {
            // Clamp in the frame the user actually sees (the canvas
            // scroller's visible rect), not the wrapper's layout
            // frame: when the page/scroller is scrolled, the two
            // frames are offset by hundreds of px and the wrapper-
            // frame clamp strands regions of the zoomed check.
            // Unmeasurable frame (no scroller ancestor, degenerate
            // rect) keeps the historical wrapper-frame clamp.
            const frame = pinchViewportFrame(el, origin);
            next = frame
                ? clampPinchPan(next, frame.w, frame.h, box.visualW, box.visualH, 48, frame.offsetX, frame.offsetY)
                : clampPinchPan(next, box.boxW, box.boxH, box.visualW, box.visualH);
        }
        last = pts;
        opts.onMove?.(next);
    };

    const onTouchStart = (e: TouchEvent) => {
        if (e.touches.length < 2) return; // single finger: fabric's world, untouched
        e.preventDefault();
        e.stopPropagation();
        if (!active) begin(e);
        else { last = points(e); lastPair = pairOf(e); }
    };
    const onTouchMove = (e: TouchEvent) => {
        if (!active) return;
        // Swallow EVERY move while the pinch is live - including the
        // one-finger tail after a finger lifts - so fabric's frozen
        // drag can never lurch when the gesture ends.
        e.preventDefault();
        e.stopPropagation();
        stepTo(e);
    };
    const onTouchEnd = (e: TouchEvent) => {
        if (!active) return;
        if (e.touches.length >= 1) {
            // Still fingers down: stay frozen, swallow the event.
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        // Last finger up: do NOT swallow - fabric's own touchend
        // finalizes its (unmoved) drag and resets its touch state.
        active = false;
        last = null;
        lastPair = '';
        el.dispatchEvent(new CustomEvent('oc:pinchend', { bubbles: true }));
        opts.onEnd?.(opts.getView());
    };

    el.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
    el.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    el.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
    el.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: false });
    return () => {
        el.removeEventListener('touchstart', onTouchStart, { capture: true } as EventListenerOptions);
        el.removeEventListener('touchmove', onTouchMove, { capture: true } as EventListenerOptions);
        el.removeEventListener('touchend', onTouchEnd, { capture: true } as EventListenerOptions);
        el.removeEventListener('touchcancel', onTouchEnd, { capture: true } as EventListenerOptions);
    };
}

/**
 * Lock out PAGE pinch-zoom while the studio is open, so the browser
 * can never scale the studio chrome over the check: the viewport
 * meta gains maximum-scale/user-scalable for the duration (honored
 * by Android Chrome), and Safari's proprietary gesture events -
 * the lever iOS actually obeys, since it ignores user-scalable -
 * are preventDefaulted at document level. Restores the original
 * meta verbatim on cleanup. Safe to call on desktop (the meta is
 * inert there) and safe when no viewport meta exists.
 */
export function lockStudioViewport(): () => void {
    if (typeof document === 'undefined') return () => { };
    const meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    const prev = meta ? meta.getAttribute('content') : null;
    if (meta) {
        meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }
    const prevent = (e: Event) => { e.preventDefault(); };
    document.addEventListener('gesturestart', prevent as EventListener, { passive: false });
    document.addEventListener('gesturechange', prevent as EventListener, { passive: false });
    return () => {
        if (meta) {
            if (prev === null) meta.removeAttribute('content');
            else meta.setAttribute('content', prev);
        }
        document.removeEventListener('gesturestart', prevent as EventListener);
        document.removeEventListener('gesturechange', prevent as EventListener);
    };
}
